import { useContext, useMemo } from 'react';
import AsyncSelect from 'react-select/async';

import { SmartContext } from '../Core/SmartContext';
import { getControlValueFromState, handleControlValueChange } from '../Core/SmartFunctions';
import { DomainElement, SimpleFormControlArguments, State } from '../Core/SmartTypes';
import './custom-control.css';
import ErrorControl from './ErrorControl';

const AsyncSelectControl = ({ control, dataKey, parentDataKey }: SimpleFormControlArguments) => {
    const { state, dispatch } = useContext(SmartContext);
    const data = getControlValueFromState(dataKey, state as State, []);

    const allOptions = useMemo(() => {
        const domain = (state?.domain?.get(control.props.domainCategoryCode) || []) as DomainElement[];
        return domain.map((d: any) => ({ value: d.code, label: d.value }));
    }, [state?.domain, control.props.domainCategoryCode]);

    const loadOptions = (inputValue: string) =>
        new Promise<{ value: string; label: string }[]>((resolve) => {
            const term = (inputValue || '').trim().toLowerCase();
            if (!term) {
                resolve(allOptions.slice(0, 50));
                return;
            }
            resolve(allOptions.filter((o) => o.label?.toLowerCase().includes(term)).slice(0, 100));
        });

    const selectedValue = useMemo(() => {
        if (!Array.isArray(data)) return [];
        return data
            .map((item: any) => {
                const code = item?.[control.id];
                return allOptions.find((o) => o.value == code);
            })
            .filter(Boolean) as { value: string; label: string }[];
    }, [data, allOptions, control.id]);

    const handleOnChange = (selectedOptions: any) => {
        const next = Array.isArray(selectedOptions)
            ? selectedOptions.map((opt: any) => ({ [control.id]: opt.value }))
            : [];
        handleControlValueChange({ control, value: next, dataKey, parentDataKey, state, dispatch });
    };

    return (
        <>
            {control.props?.label && (
                <label htmlFor={control.id} className="form-label">
                    {`${control.props.label} `}
                </label>
            )}
            <div
                className="custom-input-group custom-input-wrap"
                style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', alignItems: 'stretch' }}>
                <div
                    className="custom-input-group-text bg-white d-flex align-items-center justify-content-center border"
                    style={{ padding: '0.375rem 0.75rem' }}>
                    <i className="bi bi-search fs-6"></i>
                </div>
                <AsyncSelect
                    isMulti
                    cacheOptions
                    defaultOptions={allOptions.slice(0, 50)}
                    loadOptions={loadOptions}
                    value={selectedValue}
                    onChange={handleOnChange}
                    classNamePrefix="select"
                    placeholder="Type to search institutes..."
                    components={{ DropdownIndicator: null, IndicatorSeparator: null }}
                    isClearable
                />
            </div>
            <ErrorControl errorMessages={state?.formValidationErrors[dataKey]} />
        </>
    );
};

export default AsyncSelectControl;
